<template>
    <!-- Filters -->
    <div class="mb-4 flex gap-4 flex-wrap">
        <div class="flex-1 min-w-[250px]">
            <IconField iconPosition="left">
                <InputIcon class="fa-solid fa-search"></InputIcon>
                <InputText :model-value="search" :placeholder="t('resources.searchByName')" class="w-full"
                    @input="$emit('search', ($event.target as HTMLInputElement).value)" />
            </IconField>
        </div>
        <div class="min-w-[200px]">
            <Select :model-value="statusFilter" :options="statusOptions" optionLabel="label" optionValue="value"
                :placeholder="t('status.filterByStatus')" class="w-full"
                @change="$emit('statusChange', $event.value)" />
        </div>
        <Button icon="fa-solid fa-plus" :label="t('resources.newResource')" @click="$emit('create')" />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card v-for="n in 6" :key="n">
            <template #content>
                <Skeleton height="200px" />
            </template>
        </Card>
    </div>

    <!-- Empty State -->
    <div v-else-if="resources.length === 0" class="text-center py-12 text-gray-500">
        <i class="fa-regular fa-folder-open text-3xl mb-3"></i>
        <p class="font-semibold">{{ t('resources.noResourcesFound') }}</p>
    </div>

    <!-- Cards Grid -->
    <div v-else>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card v-for="resource in resources" :key="resource.id" class="hover:shadow-lg transition-shadow">
                <template #header>
                    <div class="p-3 bg-primary-50 border-b">
                        <h3 class="text-2xl mb-1 font-bold text-primary-700">{{ resource.name }}</h3>
                        <p class="text-gray-600">{{ resource.type.name }}</p>
                    </div>
                </template>
                <template #content>
                    <div class="flex items-center gap-2">
                        <i class="fa-solid fa-location-dot text-gray-500 text-lg"></i>
                        <span class="">{{ resource.location || '-' }}</span>
                    </div>
                </template>
                <template #footer>
                    <div class="flex justify-between items-center pt-1">
                        <Tag :value="resource.enabled ? t('common.enabled') : t('common.disabled')"
                            :severity="resource.enabled ? 'success' : 'danger'" class="px-3 py-1" />
                        <div class="flex gap-1">
                            <Button icon="fa-solid fa-edit" text rounded severity="primary" size="large"
                                @click="$emit('edit', resource)" />
                            <Button icon="fa-regular fa-trash-can" text rounded severity="danger" size="large"
                                @click="$emit('delete', resource)" />
                        </div>
                    </div>
                </template>
            </Card>
        </div>

        <!-- Paginator -->
        <Paginator :first="first" :rows="rows" :totalRecords="totalRecords" @page="$emit('page', $event)" class="mt-4"
            style="border: 1px solid var(--surface-border)" />
    </div>
</template>

<script setup lang="ts">
import type { Resource } from '@/lib/types/resource';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
    resources: Resource[];
    loading: boolean;
    search: string;
    statusFilter: boolean | null;
    first: number;
    rows: number;
    totalRecords: number;
}

defineProps<Props>();

defineEmits<{
    search: [value: string];
    statusChange: [value: boolean | null];
    page: [event: any];
    create: [];
    edit: [resource: Resource];
    delete: [resource: Resource];
}>();

const statusOptions = computed(() => [
    { label: t('status.all'), value: null },
    { label: t('common.enabled'), value: true },
    { label: t('common.disabled'), value: false }
]);
</script>
